import { useState, useEffect, useCallback, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { invalidateTaskQueries, getDisplayName } from "@/lib/task-query-utils";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Plus,
  Calendar as CalendarIcon,
  X,
  CheckSquare,
  PawPrint,
  Link2,
  MessageSquare,
  ClipboardList,
} from "lucide-react";
import { getBoardEntityLabel, getBoardRoutePath } from "@/lib/board-types";
import { AnimalSearchSelect } from "@/components/tasks/animal-search-select";
import { InteractionSearchSelect } from "@/components/tasks/interaction-search-select";
import { TaskSearchSelect } from "@/components/tasks/task-search-select";

interface Status {
  id: string;
  name: string;
  sortOrder: number;
  isDefault?: boolean;
}

interface BoardLabel {
  id: string;
  name: string;
  color: string | null;
}

interface BoardMember {
  userId: string;
  role: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
}

export interface CreateTaskFormProps {
  boardId: string;
  statuses: Status[];
  labels: BoardLabel[];
  members: BoardMember[];
  initialAnimalIds?: string[];
  initialAnimalNames?: Map<string, string>;
  initialInteractionIds?: string[];
  initialInteractionNames?: Map<string, string>;
  initialStatusId?: string;
  boardType?: string;
  onCreated?: (taskId: string) => void;
  initialTitle?: string;
  initialDescription?: string;
  initialPriority?: string | null;
  initialLabelIds?: string[];
  initialNote?: string;
  templateId?: string;
  showNote?: boolean;
  onCancel?: () => void;
  onClose?: () => void;
  compact?: boolean;
  active?: boolean;
  onRequestCloseRef?: React.MutableRefObject<((action: "cancel" | "close") => void) | null>;
}

export function CreateTaskForm({
  boardId,
  statuses,
  labels,
  members,
  initialAnimalIds,
  initialAnimalNames,
  initialInteractionIds,
  initialInteractionNames,
  initialStatusId,
  boardType,
  onCreated,
  initialTitle,
  initialDescription,
  initialPriority,
  initialLabelIds,
  initialNote,
  templateId,
  showNote = false,
  onCancel,
  onClose,
  compact = false,
  active = true,
  onRequestCloseRef,
}: CreateTaskFormProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const [title, setTitle] = useState(initialTitle || "");
  const [description, setDescription] = useState(initialDescription || "");
  const [statusId, setStatusId] = useState(initialStatusId || "");
  const [assigneeUserId, setAssigneeUserId] = useState<string>("__none__");
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [priority, setPriority] = useState<string>(
    initialPriority && initialPriority !== null ? initialPriority : "none"
  );
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>(initialLabelIds || []);
  const [selectedAnimalIds, setSelectedAnimalIds] = useState<string[]>(initialAnimalIds || []);
  const [selectedAnimalNames, setSelectedAnimalNames] = useState<Map<string, string>>(
    initialAnimalNames || new Map()
  );
  const [selectedInteractionIds, setSelectedInteractionIds] = useState<string[]>(
    initialInteractionIds || []
  );
  const [selectedInteractionNames, setSelectedInteractionNames] = useState<Map<string, string>>(
    initialInteractionNames || new Map()
  );
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [reminderDate, setReminderDate] = useState<Date | undefined>(undefined);
  const [reminderDatePickerOpen, setReminderDatePickerOpen] = useState(false);
  const [reminderTime, setReminderTime] = useState("");
  const [note, setNote] = useState(initialNote || "");
  const [activeLinkType, setActiveLinkType] = useState<"animal" | "interaction" | "task" | null>(null);
  const [linkedTaskId, setLinkedTaskId] = useState<string | null>(null);
  const [linkedTaskLabel, setLinkedTaskLabel] = useState("");
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const pendingCloseAction = useRef<"cancel" | "close" | null>(null);

  useEffect(() => {
    if (initialTitle) setTitle(initialTitle);
    if (initialDescription) setDescription(initialDescription);
    if (initialStatusId) setStatusId(initialStatusId);
    if (initialPriority && initialPriority !== null) setPriority(initialPriority);
    if (initialLabelIds?.length) setSelectedLabelIds(initialLabelIds);
    if (initialAnimalIds?.length) setSelectedAnimalIds(initialAnimalIds);
    if (initialAnimalNames?.size) setSelectedAnimalNames(initialAnimalNames);
    if (initialInteractionIds?.length) setSelectedInteractionIds(initialInteractionIds);
    if (initialInteractionNames?.size) setSelectedInteractionNames(initialInteractionNames);
    if (initialNote) setNote(initialNote);
  }, [boardId]);

  const isDirty = useCallback(() => {
    if (title.trim() !== (initialTitle || "").trim()) return true;
    if (description.trim() !== (initialDescription || "").trim()) return true;
    const initPriority = (initialPriority && initialPriority !== null) ? initialPriority : "none";
    if (priority !== initPriority) return true;
    if (assigneeUserId !== "__none__") return true;
    if (dueDate !== undefined) return true;
    if (reminderDate !== undefined) return true;
    if (reminderTime) return true;
    const initLabelIds = initialLabelIds || [];
    if (selectedLabelIds.length !== initLabelIds.length || !selectedLabelIds.every(id => initLabelIds.includes(id))) return true;
    const initAnimalIds = initialAnimalIds || [];
    if (selectedAnimalIds.length !== initAnimalIds.length || !selectedAnimalIds.every(id => initAnimalIds.includes(id))) return true;
    const initInteractionIds = initialInteractionIds || [];
    if (selectedInteractionIds.length !== initInteractionIds.length || !selectedInteractionIds.every(id => initInteractionIds.includes(id))) return true;
    if (showNote && note.trim() !== (initialNote || "").trim()) return true;
    return false;
  }, [title, description, priority, assigneeUserId, dueDate, selectedLabelIds, selectedAnimalIds, selectedInteractionIds, note, showNote, initialTitle, initialDescription, initialPriority, initialLabelIds, initialAnimalIds, initialInteractionIds, initialNote]);

  const requestClose = useCallback((action: "cancel" | "close") => {
    if (isDirty()) {
      pendingCloseAction.current = action;
      setShowUnsavedWarning(true);
    } else {
      if (action === "cancel" && onCancel) onCancel();
      else if (action === "close" && onClose) onClose();
    }
  }, [isDirty, onCancel, onClose]);

  const handleUnsavedDiscard = useCallback(() => {
    setShowUnsavedWarning(false);
    const action = pendingCloseAction.current;
    pendingCloseAction.current = null;
    if (action === "cancel" && onCancel) onCancel();
    else if (action === "close" && onClose) onClose();
    else if (onCancel) onCancel();
    else if (onClose) onClose();
  }, [onCancel, onClose]);

  const handleUnsavedStay = useCallback(() => {
    setShowUnsavedWarning(false);
    pendingCloseAction.current = null;
  }, []);

  useEffect(() => {
    if (!active) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty()) {
        e.preventDefault();
        return "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [active, isDirty]);

  useEffect(() => {
    if (onRequestCloseRef) {
      onRequestCloseRef.current = requestClose;
    }
    return () => {
      if (onRequestCloseRef) {
        onRequestCloseRef.current = null;
      }
    };
  }, [onRequestCloseRef, requestClose]);

  const defaultStatus = statuses.find(s => (s as any).isDefault) ?? statuses[0];

  const { data: templateData } = useQuery<{ task: { checklistItems?: Array<{ id: string; text: string; isDone: boolean; sortOrder: number }> } }>({
    queryKey: ["/api/tasks", templateId],
    enabled: !!templateId && active,
  });

  const templateChecklistItems = templateData?.task?.checklistItems || [];

  const appendNoteMutation = useMutation({
    mutationFn: async (taskId: string) => {
      return await apiRequest("POST", `/api/notes/task/${taskId}/append`, {
        text: note.trim(),
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = {
        boardId,
        title: title.trim(),
      };
      if (description.trim()) body.description = description.trim();
      const resolvedStatusId = statusId || defaultStatus?.id;
      if (resolvedStatusId) body.statusId = resolvedStatusId;
      if (assigneeUserId !== "__none__") body.assigneeUserId = assigneeUserId;
      if (dueDate) body.dueDate = dueDate.toISOString().split("T")[0];
      if (priority !== "none") body.priority = priority;
      if (selectedAnimalIds.length > 0) body.animalIds = selectedAnimalIds;
      if (selectedInteractionIds.length > 0) body.interactionIds = selectedInteractionIds;
      if (linkedTaskId) body.linkedTaskId = linkedTaskId;
      if (selectedLabelIds.length > 0) body.labelIds = selectedLabelIds;
      if (templateId) body.templateId = templateId;
      if (reminderDate) body.reminderDate = reminderDate.toISOString().split("T")[0];
      if (reminderTime) body.reminderTime = reminderTime;
      return await apiRequest("POST", "/api/tasks", body);
    },
    onSuccess: async (data: any) => {
      invalidateTaskQueries();
      if (isReminder) {
        queryClient.invalidateQueries({ queryKey: ["/api/tasks/my-reminders"] });
      }
      const taskId = data?.task?.id;
      const createdTitle = data?.task?.title || title.trim();

      if (taskId && note.trim()) {
        try {
          await appendNoteMutation.mutateAsync(taskId);
        } catch {
        }
      }

      toast({
        title: `New ${getBoardEntityLabel(boardType)}: ${createdTitle}`,
        action: taskId && !onCreated
          ? <ToastAction altText="View" onClick={() => navigate(`${getBoardRoutePath(boardType)}/${taskId}`)}>View</ToastAction>
          : undefined,
      });
      resetForm();
      if (onClose) {
        onClose();
      }
      if (taskId && onCreated) {
        onCreated(taskId);
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function resetForm() {
    setTitle("");
    setDescription("");
    setStatusId("");
    setAssigneeUserId("__none__");
    setDueDate(undefined);
    setReminderDate(undefined);
    setReminderTime("");
    setPriority("none");
    setSelectedLabelIds([]);
    setSelectedAnimalIds([]);
    setSelectedAnimalNames(new Map());
    setSelectedInteractionIds([]);
    setSelectedInteractionNames(new Map());
    setNote("");
  }

  function toggleLabel(labelId: string) {
    setSelectedLabelIds(prev =>
      prev.includes(labelId) ? prev.filter(id => id !== labelId) : [...prev, labelId]
    );
  }


  const isReminder = boardType === "reminder";
  const entityLabel = getBoardEntityLabel(boardType);
  const spacing = compact ? "space-y-3" : "space-y-4";

  return (
    <div className={spacing} data-testid="create-task-form">
      <div className="space-y-2">
        <Label htmlFor="create-title">Title *</Label>
        <Input
          id="create-title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder={`Enter ${entityLabel.toLowerCase()} title...`}
          maxLength={500}
          data-testid="task-input-create-title"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="create-description">Description {isReminder && <span className="font-normal italic text-muted-foreground">(optional)</span>}</Label>
        <Textarea
          id="create-description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Add details..."
          className="resize-none"
          maxLength={5000}
          data-testid="task-textarea-create-description"
        />
      </div>

      {!isReminder && (
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={statusId || defaultStatus?.id || ""} onValueChange={setStatusId}>
            <SelectTrigger data-testid="task-select-create-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statuses.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {!isReminder && (
        <div className="space-y-2">
          <Label>Assignee</Label>
          <Select value={assigneeUserId} onValueChange={setAssigneeUserId}>
            <SelectTrigger data-testid="task-select-create-assignee">
              <SelectValue placeholder="Unassigned" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Unassigned</SelectItem>
              {members.map(m => (
                <SelectItem key={m.userId} value={m.userId}>{getDisplayName(m.firstName, m.lastName, m.username)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {!isReminder && (
        <div className="space-y-2">
          <Label>Priority</Label>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger data-testid="task-select-create-priority">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {isReminder ? (
        <>
          <div className="space-y-2">
            <Label>Reminder Date *</Label>
            <div className="flex items-center gap-2">
              <Popover open={reminderDatePickerOpen} onOpenChange={setReminderDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="task-button-create-reminder-date">
                    <CalendarIcon className="w-3 h-3 mr-1" />
                    {reminderDate ? reminderDate.toLocaleDateString() : "Set date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={reminderDate}
                    onSelect={d => { setReminderDate(d); setReminderDatePickerOpen(false); }}
                  />
                </PopoverContent>
              </Popover>
              {reminderDate && (
                <Button variant="ghost" size="sm" onClick={() => setReminderDate(undefined)} data-testid="task-button-clear-create-reminder-date">
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
          {reminderTime ? (
            <div className="space-y-2">
              <Label htmlFor="create-reminder-time">Time <span className="font-normal italic text-muted-foreground">(optional)</span></Label>
              <div className="flex items-center gap-1">
                <Input
                  id="create-reminder-time"
                  type="time"
                  value={reminderTime}
                  onChange={e => setReminderTime(e.target.value)}
                  className="w-[140px]"
                  data-testid="task-input-create-reminder-time"
                />
                <Button type="button" variant="ghost" size="icon" onClick={() => setReminderTime("")} data-testid="task-button-clear-reminder-time">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setReminderTime("12:00")}
              data-testid="task-button-add-reminder-time"
            >
              <CalendarIcon className="w-3 h-3 mr-1" />
              Add time
            </Button>
          )}
        </>
      ) : (
        <div className="space-y-2">
          <Label>Due Date</Label>
          <div className="flex items-center gap-2">
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="task-button-create-due-date">
                  <CalendarIcon className="w-3 h-3 mr-1" />
                  {dueDate ? dueDate.toLocaleDateString() : "Set date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={d => { setDueDate(d); setDatePickerOpen(false); }}
                />
              </PopoverContent>
            </Popover>
            {dueDate && (
              <Button variant="ghost" size="sm" onClick={() => setDueDate(undefined)} data-testid="task-button-clear-create-due-date">
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
      )}

      {!isReminder && (
        <div className="space-y-2">
          <Label>Labels</Label>
          <div className="flex flex-wrap gap-1">
            {labels.map(l => (
              <Badge
                key={l.id}
                variant={selectedLabelIds.includes(l.id) ? "default" : "outline"}
                className="cursor-pointer text-xs toggle-elevate"
                onClick={() => toggleLabel(l.id)}
                data-testid={`task-badge-create-label-${l.id}`}
              >
                {l.color && <span className="w-2 h-2 rounded-full mr-1 inline-block" style={{ backgroundColor: l.color }} />}
                {l.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {templateId && templateChecklistItems.length > 0 && (
        <div className="space-y-2">
          <Label className="flex items-center gap-1">
            <CheckSquare className="w-3.5 h-3.5" />
            Checklist Items
          </Label>
          <div className="border rounded-md p-2 space-y-1">
            {templateChecklistItems.map((item, idx) => (
              <div key={item.id || idx} className="flex items-center gap-2 text-sm text-muted-foreground" data-testid={`task-checklist-preview-${idx}`}>
                <div className="w-3.5 h-3.5 border rounded-sm flex-shrink-0" />
                <span>{item.text}</span>
              </div>
            ))}
            <p className="text-xs text-muted-foreground mt-2 italic">
              Checklist items can be edited after creation.
            </p>
          </div>
        </div>
      )}

      {isReminder ? (
        <>
          {(selectedAnimalIds.length > 0 || selectedInteractionIds.length > 0 || linkedTaskId) && (
            <div className="flex flex-wrap gap-1" data-testid="task-create-linked-items">
              {selectedAnimalIds.map(id => (
                <Badge key={`a-${id}`} variant="secondary" className="text-xs" data-testid={`task-create-badge-animal-${id}`}>
                  <PawPrint className="w-3 h-3 mr-1" />
                  {selectedAnimalNames.get(id) || id}
                  <button className="ml-1" onClick={() => {
                    setSelectedAnimalIds(prev => prev.filter(a => a !== id));
                    setSelectedAnimalNames(prev => { const n = new Map(prev); n.delete(id); return n; });
                  }}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {selectedInteractionIds.map(id => (
                <Badge key={`i-${id}`} variant="secondary" className="text-xs" data-testid={`task-create-badge-interaction-${id}`}>
                  <Link2 className="w-3 h-3 mr-1" />
                  {selectedInteractionNames.get(id) || id.slice(0, 8)}
                  <button className="ml-1" onClick={() => {
                    setSelectedInteractionIds(prev => prev.filter(i => i !== id));
                    setSelectedInteractionNames(prev => { const n = new Map(prev); n.delete(id); return n; });
                  }}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {linkedTaskId && (
                <Badge variant="secondary" className="text-xs" data-testid="task-create-badge-linked-task">
                  <ClipboardList className="w-3 h-3 mr-1" />
                  {linkedTaskLabel}
                  <button className="ml-1" onClick={() => { setLinkedTaskId(null); setLinkedTaskLabel(""); }}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
            </div>
          )}

          {activeLinkType === null ? (
            <div className="flex items-center gap-2 flex-wrap" data-testid="task-create-link-picker">
              <span className="text-xs text-muted-foreground">Link to:</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setActiveLinkType("animal")}
                data-testid="task-create-button-link-animal"
              >
                <PawPrint className="w-3 h-3 mr-1" />
                Animal
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setActiveLinkType("interaction")}
                data-testid="task-create-button-link-interaction"
              >
                <Link2 className="w-3 h-3 mr-1" />
                Interaction
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={linkedTaskId !== null}
                onClick={() => setActiveLinkType("task")}
                data-testid="task-create-button-link-task"
              >
                <ClipboardList className="w-3 h-3 mr-1" />
                Task/Meeting
              </Button>
            </div>
          ) : (
            <div className="space-y-2" data-testid="task-create-link-active">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  {activeLinkType === "animal" ? "Link Animal" : activeLinkType === "interaction" ? "Link Interaction" : "Link Task/Meeting"}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setActiveLinkType(null)}
                  data-testid="task-create-button-link-cancel"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
              {activeLinkType === "animal" && (
                <AnimalSearchSelect
                  selectedIds={selectedAnimalIds}
                  selectedNames={selectedAnimalNames}
                  onAdd={(id, name) => {
                    setSelectedAnimalIds(prev => [...prev, id]);
                    setSelectedAnimalNames(prev => new Map(prev).set(id, name));
                    setActiveLinkType(null);
                  }}
                  onRemove={(id) => {
                    setSelectedAnimalIds(prev => prev.filter(a => a !== id));
                    setSelectedAnimalNames(prev => { const n = new Map(prev); n.delete(id); return n; });
                  }}
                  showLabel={false}
                  testIdPrefix="task-create"
                />
              )}
              {activeLinkType === "interaction" && (
                <InteractionSearchSelect
                  selectedIds={selectedInteractionIds}
                  selectedNames={selectedInteractionNames}
                  onAdd={(id, displayName) => {
                    setSelectedInteractionIds(prev => [...prev, id]);
                    setSelectedInteractionNames(prev => new Map(prev).set(id, displayName));
                    setActiveLinkType(null);
                  }}
                  onRemove={(id) => {
                    setSelectedInteractionIds(prev => prev.filter(i => i !== id));
                    setSelectedInteractionNames(prev => { const n = new Map(prev); n.delete(id); return n; });
                  }}
                  showLabel={false}
                  testIdPrefix="task-create"
                />
              )}
              {activeLinkType === "task" && (
                <TaskSearchSelect
                  selectedId={linkedTaskId}
                  selectedLabel={linkedTaskLabel}
                  onSelect={(id, label) => {
                    setLinkedTaskId(id);
                    setLinkedTaskLabel(label);
                    setActiveLinkType(null);
                  }}
                  onClear={() => { setLinkedTaskId(null); setLinkedTaskLabel(""); }}
                  showLabel={false}
                  testIdPrefix="task-create"
                />
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <AnimalSearchSelect
            selectedIds={selectedAnimalIds}
            selectedNames={selectedAnimalNames}
            onAdd={(id, name) => {
              if (!selectedAnimalIds.includes(id)) {
                setSelectedAnimalIds(prev => [...prev, id]);
                setSelectedAnimalNames(prev => new Map(prev).set(id, name));
              }
            }}
            onRemove={(id) => {
              setSelectedAnimalIds(prev => prev.filter(a => a !== id));
              setSelectedAnimalNames(prev => { const n = new Map(prev); n.delete(id); return n; });
            }}
            enabled={active}
            testIdPrefix="task-create"
          />

          <InteractionSearchSelect
            selectedIds={selectedInteractionIds}
            selectedNames={selectedInteractionNames}
            onAdd={(id, displayName) => {
              if (!selectedInteractionIds.includes(id)) {
                setSelectedInteractionIds(prev => [...prev, id]);
                setSelectedInteractionNames(prev => new Map(prev).set(id, displayName));
              }
            }}
            onRemove={(id) => {
              setSelectedInteractionIds(prev => prev.filter(i => i !== id));
              setSelectedInteractionNames(prev => { const n = new Map(prev); n.delete(id); return n; });
            }}
            enabled={active}
            testIdPrefix="task-create"
          />
        </>
      )}

      {showNote && !isReminder && (
        <div className="space-y-2">
          <Label htmlFor="create-note">Notes</Label>
          <Textarea
            id="create-note"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Add a note..."
            className="resize-none"
            rows={3}
            data-testid="task-textarea-create-note"
          />
        </div>
      )}

      <AlertDialog open={showUnsavedWarning} onOpenChange={(open) => { if (!open) handleUnsavedStay(); }}>
        <AlertDialogContent data-testid="dialog-unsaved-changes">
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. If you leave now, your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleUnsavedStay} data-testid="button-stay-editing">Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnsavedDiscard} data-testid="button-discard-changes">Discard Changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex justify-end gap-2 pt-4">
        {onCancel && (
          <Button variant="outline" onClick={() => requestClose("cancel")} data-testid="task-button-cancel-create">
            Cancel
          </Button>
        )}
        <Button
          onClick={() => createMutation.mutate()}
          disabled={!title.trim() || (isReminder && !reminderDate) || createMutation.isPending}
          data-testid="task-button-submit-create"
        >
          <Plus className="w-4 h-4 mr-1" />
          Create {entityLabel}
        </Button>
      </div>
    </div>
  );
}
